import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { orderService } from '../services/orderService';
import { paymentService } from '../services/paymentService';
import { couponService } from '../services/couponService';
import { getProductPrice, getCommission, getDeliveryFee, formatPrice } from '../utils/priceHelpers';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const Payment = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState('stripe'); // 'stripe' ou 'mobile-money'
  const [mobileMoneyProvider, setMobileMoneyProvider] = useState('orange'); // 'orange', 'airtel', 'mpesa', 'afrimoney'
  const [phoneNumber, setPhoneNumber] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [couponError, setCouponError] = useState(null);

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const response = await orderService.getOrderById(orderId);
      if (response.success && response.order) {
        // Vérifier que la commande appartient à l'utilisateur
        const ownerUid = response.order.userUid || response.order.userId;
        const currentUid = user?.id || user?.uid || localStorage.getItem('userId');
        if (ownerUid && currentUid && ownerUid !== currentUid) {
          navigate('/dashboard');
          return;
        }
        const amount =
          Number(response.order.finalAmount ?? response.order.totalAmount) || 0;
        if (amount <= 0) {
          setError('Le prix de cette commande n\'a pas encore été défini par un agent');
        }
        // Vérifier que la commande n'est pas déjà payée
        if (response.order.paymentStatus === 'Payé') {
          setError('Cette commande a déjà été payée');
        }
        setOrder(response.order);
      } else {
        setError('Commande non trouvée');
      }
    } catch (err) {
      console.error('Erreur lors de la récupération de la commande:', err);
      setError('Erreur lors de la récupération de la commande');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Veuillez entrer un code coupon');
      return;
    }

    setValidatingCoupon(true);
    setCouponError(null);

    try {
      const orderAmount = order?.finalAmount || order?.totalAmount || 0;
      const response = await couponService.validateCoupon(
        couponCode.trim(),
        orderAmount,
        orderId,
        order?.platform
      );

      if (response.success) {
        // Appliquer le coupon à la commande
        const applyResponse = await couponService.applyCoupon(couponCode.trim(), orderId);
        if (applyResponse.success) {
          setAppliedCoupon({
            code: response.coupon.code,
            name: response.coupon.name,
            discountAmount: response.discountAmount,
            finalAmount: response.finalAmount,
          });
          // Recharger la commande pour obtenir les nouvelles valeurs
          await fetchOrder();
          setCouponCode('');
        } else {
          setCouponError(applyResponse.error || 'Erreur lors de l\'application du coupon');
        }
      } else {
        setCouponError(response.error || 'Code coupon invalide');
      }
    } catch (err) {
      console.error('Erreur lors de la validation du coupon:', err);
      setCouponError('Une erreur est survenue lors de la validation du coupon');
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleRemoveCoupon = async () => {
    // Pour retirer un coupon, on devrait avoir une route API
    // Pour l'instant, on peut juste réinitialiser l'état
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError(null);
    // Recharger la commande
    await fetchOrder();
  };

  const handlePayment = async () => {
    const payAmount = Number(order.finalAmount ?? order.totalAmount) || 0;
    if (!order || payAmount <= 0) {
      setError('Le prix de la commande n\'est pas disponible');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Utiliser le montant final (avec coupon si appliqué)
      const amountToPay = order.finalAmount || order.totalAmount;

      if (paymentMethod === 'stripe') {
        // Créer le lien de paiement Stripe et rediriger
        const result = await paymentService.createStripePayment(orderId);
        if (result.success && result.paymentLink) {
          window.location.href = result.paymentLink;
        } else {
          setError(result.error || 'Erreur lors de la création du paiement');
          setProcessing(false);
        }
      } else if (paymentMethod === 'mobile-money') {
        if (!phoneNumber || phoneNumber.trim() === '') {
          setError('Veuillez entrer votre numéro de téléphone');
          setProcessing(false);
          return;
        }
        // Créer le paiement Mobile Money avec le provider sélectionné
        const result = await paymentService.createMobileMoneyPayment(
          orderId,
          phoneNumber,
          mobileMoneyProvider
        );
        if (result.success) {
          toast.info(
            result.instructions || result.message || 'Paiement initié. Confirmez sur votre téléphone.',
            10000
          );
          if (result.pending !== false) {
            toast.info('Vérification du paiement en cours…', 5000);
            for (let i = 0; i < 12; i += 1) {
              await new Promise((r) => setTimeout(r, 10000));
              const check = await paymentService.verifySerdiPayPayment(orderId);
              if (check.success && check.paid) {
                toast.success('Paiement confirmé !');
                navigate('/dashboard');
                return;
              }
            }
            toast.info('Paiement en attente de confirmation. Consultez votre dashboard.');
          }
          navigate('/dashboard');
        } else {
          setError(result.error || 'Erreur lors de la création du paiement');
          setProcessing(false);
        }
      }
    } catch (err) {
      console.error('Erreur lors du paiement:', err);
      setError('Une erreur est survenue lors du traitement du paiement');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="container">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ color: '#dc3545' }}>Commande non trouvée</p>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/dashboard')}
            style={{ marginTop: '20px' }}
          >
            Retour au dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />

      <div className="container">
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <h1 className="dashboard-title" style={{ textAlign: 'center', marginBottom: '30px' }}>
            💳 Paiement
          </h1>

          {/* Détails de la commande */}
          <div className="card" style={{ marginBottom: '30px' }}>
            <h3 style={{ marginBottom: '15px', color: '#333' }}>
              Commande #{order._id.slice(-8).toUpperCase()}
            </h3>
            {order.verifiedProductName && (
              <p style={{ color: '#666', marginBottom: '10px' }}>
                <strong>Produit:</strong> {order.verifiedProductName}
              </p>
            )}
            <div style={{ marginTop: '15px', padding: '15px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
              {(() => {
                const hasMultipleProducts = order.totalProductPrice != null || 
                                          order.totalCommission != null || 
                                          order.totalDeliveryFee != null ||
                                          (order.items && Array.isArray(order.items) && order.items.length > 0);
                
                const productPrice = getProductPrice(order);
                const commission = getCommission(order);
                const deliveryFee = getDeliveryFee(order);
                
                return (
                  <>
                    <p style={{ margin: '5px 0', fontSize: '14px' }}>
                      <strong>Prix {hasMultipleProducts ? 'total produits' : 'produit'}:</strong> ${formatPrice(productPrice)}
                    </p>
                    <p style={{ margin: '5px 0', fontSize: '14px' }}>
                      <strong>Commission{hasMultipleProducts ? ' totale' : ''}:</strong> ${formatPrice(commission)}
                    </p>
                    <p style={{ margin: '5px 0', fontSize: '14px' }}>
                      <strong>Frais livraison:</strong> ${formatPrice(deliveryFee)}
                    </p>
                  </>
                );
              })()}
              {appliedCoupon && (
                <>
                  <p style={{ margin: '5px 0', fontSize: '14px', color: '#28a745' }}>
                    <strong>Coupon ({appliedCoupon.code}):</strong> -${appliedCoupon.discountAmount?.toFixed(2) || '0.00'}
                  </p>
                  <p style={{ margin: '10px 0 5px 0', fontSize: '20px', fontWeight: 'bold', color: '#FF0000', borderTop: '1px solid #bae6fd', paddingTop: '10px' }}>
                    <strong>Total à payer:</strong> ${(order.finalAmount || order.totalAmount)?.toFixed(2) || '0.00'}
                  </p>
                </>
              )}
              {!appliedCoupon && (
                <p style={{ margin: '10px 0 5px 0', fontSize: '20px', fontWeight: 'bold', color: '#FF0000', borderTop: '1px solid #bae6fd', paddingTop: '10px' }}>
                  <strong>Total à payer:</strong> ${order.totalAmount?.toFixed(2) || '0.00'}
                </p>
              )}
            </div>
          </div>

          {/* Section coupon */}
          {!appliedCoupon && (
            <div className="card" style={{ marginBottom: '30px' }}>
              <h3 style={{ marginBottom: '15px', color: '#333' }}>🎟️ Code promotionnel</h3>
              <div style={{ display: 'flex', gap: '10px' }}>
                <input
                  type="text"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  placeholder="Entrez votre code coupon"
                  style={{
                    flex: 1,
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '16px',
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleApplyCoupon();
                    }
                  }}
                />
                <button
                  onClick={handleApplyCoupon}
                  disabled={validatingCoupon || !couponCode.trim()}
                  className="btn btn-primary"
                  style={{
                    whiteSpace: 'nowrap',
                    opacity: (validatingCoupon || !couponCode.trim()) ? 0.6 : 1,
                    cursor: (validatingCoupon || !couponCode.trim()) ? 'not-allowed' : 'pointer',
                  }}
                >
                  {validatingCoupon ? 'Validation...' : 'Appliquer'}
                </button>
              </div>
              {couponError && (
                <p style={{ marginTop: '10px', color: '#dc3545', fontSize: '14px' }}>
                  {couponError}
                </p>
              )}
            </div>
          )}

          {/* Coupon appliqué */}
          {appliedCoupon && (
            <div className="card" style={{ marginBottom: '30px', backgroundColor: '#d4edda', border: '1px solid #c3e6cb' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h3 style={{ margin: '0 0 5px 0', color: '#155724' }}>✅ Coupon appliqué</h3>
                  <p style={{ margin: 0, color: '#155724', fontSize: '14px' }}>
                    <strong>{appliedCoupon.code}</strong> - {appliedCoupon.name}
                  </p>
                  <p style={{ margin: '5px 0 0 0', color: '#155724', fontSize: '14px' }}>
                    Réduction: ${appliedCoupon.discountAmount?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <button
                  onClick={handleRemoveCoupon}
                  className="btn"
                  style={{
                    backgroundColor: '#dc3545',
                    color: '#fff',
                    border: 'none',
                    padding: '8px 15px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  Retirer
                </button>
              </div>
            </div>
          )}

          {/* Erreur */}
          {error && (
            <div style={{ 
              padding: '15px', 
              backgroundColor: '#fee', 
              border: '1px solid #fcc', 
              borderRadius: '8px', 
              color: '#c33',
              marginBottom: '20px'
            }}>
              {error}
            </div>
          )}

          {/* Méthode de paiement */}
          <div className="card" style={{ marginBottom: '20px' }}>
            <h3 style={{ marginBottom: '20px', color: '#333' }}>Choisir une méthode de paiement</h3>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: '15px' }}>
                <input
                  type="radio"
                  name="paymentMethod"
                  value="stripe"
                  checked={paymentMethod === 'stripe'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  style={{ marginRight: '10px', width: '20px', height: '20px' }}
                />
                <div>
                  <strong>💳 Carte bancaire (Stripe)</strong>
                  <p style={{ margin: '5px 0', color: '#666', fontSize: '14px' }}>
                    Paiement sécurisé par carte de crédit ou débit
                  </p>
                </div>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="paymentMethod"
                  value="mobile-money"
                  checked={paymentMethod === 'mobile-money'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  style={{ marginRight: '10px', width: '20px', height: '20px' }}
                />
                <div style={{ flex: 1 }}>
                  <strong>📱 Mobile Money</strong>
                  <p style={{ margin: '5px 0', color: '#666', fontSize: '14px' }}>
                    Paiement via votre compte Mobile Money
                  </p>
                </div>
              </label>
            </div>

            {paymentMethod === 'mobile-money' && (
              <div style={{ marginTop: '20px' }}>
                {/* Sélection du provider Mobile Money */}
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: '600' }}>
                  Opérateur Mobile Money
                </label>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(2, 1fr)', 
                  gap: '10px',
                  marginBottom: '20px'
                }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    cursor: 'pointer',
                    padding: '12px',
                    border: `2px solid ${mobileMoneyProvider === 'orange' ? '#FF6600' : '#ddd'}`,
                    borderRadius: '8px',
                    backgroundColor: mobileMoneyProvider === 'orange' ? '#fff5f0' : '#fff',
                    transition: 'all 0.3s ease'
                  }}>
                    <input
                      type="radio"
                      name="mobileMoneyProvider"
                      value="orange"
                      checked={mobileMoneyProvider === 'orange'}
                      onChange={(e) => setMobileMoneyProvider(e.target.value)}
                      style={{ marginRight: '10px', width: '18px', height: '18px' }}
                    />
                    <div>
                      <strong style={{ color: '#FF6600' }}>🟠 Orange Money</strong>
                    </div>
                  </label>

                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    cursor: 'pointer',
                    padding: '12px',
                    border: `2px solid ${mobileMoneyProvider === 'airtel' ? '#E60012' : '#ddd'}`,
                    borderRadius: '8px',
                    backgroundColor: mobileMoneyProvider === 'airtel' ? '#fff5f5' : '#fff',
                    transition: 'all 0.3s ease'
                  }}>
                    <input
                      type="radio"
                      name="mobileMoneyProvider"
                      value="airtel"
                      checked={mobileMoneyProvider === 'airtel'}
                      onChange={(e) => setMobileMoneyProvider(e.target.value)}
                      style={{ marginRight: '10px', width: '18px', height: '18px' }}
                    />
                    <div>
                      <strong style={{ color: '#E60012' }}>🔴 Airtel Money</strong>
                    </div>
                  </label>

                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    cursor: 'pointer',
                    padding: '12px',
                    border: `2px solid ${mobileMoneyProvider === 'mpesa' ? '#00A862' : '#ddd'}`,
                    borderRadius: '8px',
                    backgroundColor: mobileMoneyProvider === 'mpesa' ? '#f0fff4' : '#fff',
                    transition: 'all 0.3s ease'
                  }}>
                    <input
                      type="radio"
                      name="mobileMoneyProvider"
                      value="mpesa"
                      checked={mobileMoneyProvider === 'mpesa'}
                      onChange={(e) => setMobileMoneyProvider(e.target.value)}
                      style={{ marginRight: '10px', width: '18px', height: '18px' }}
                    />
                    <div>
                      <strong style={{ color: '#00A862' }}>🟢 M-Pesa</strong>
                    </div>
                  </label>

                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    cursor: 'pointer',
                    padding: '12px',
                    border: `2px solid ${mobileMoneyProvider === 'afrimoney' ? '#0066CC' : '#ddd'}`,
                    borderRadius: '8px',
                    backgroundColor: mobileMoneyProvider === 'afrimoney' ? '#f0f5ff' : '#fff',
                    transition: 'all 0.3s ease'
                  }}>
                    <input
                      type="radio"
                      name="mobileMoneyProvider"
                      value="afrimoney"
                      checked={mobileMoneyProvider === 'afrimoney'}
                      onChange={(e) => setMobileMoneyProvider(e.target.value)}
                      style={{ marginRight: '10px', width: '18px', height: '18px' }}
                    />
                    <div>
                      <strong style={{ color: '#0066CC' }}>🔵 Afrimoney</strong>
                    </div>
                  </label>
                </div>

                {/* Numéro de téléphone */}
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: '600' }}>
                  Numéro de téléphone
                </label>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="243 900 000 000 (sans le +)"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '16px',
                  }}
                />
                <p style={{ marginTop: '5px', color: '#666', fontSize: '12px' }}>
                  Format: 243XXXXXXXXX (ex: 243900123456)
                </p>
              </div>
            )}
          </div>

          {/* Bouton de paiement */}
          <button
            className="btn btn-primary"
            onClick={handlePayment}
            disabled={processing || !order.totalAmount || order.paymentStatus === 'Payé'}
            style={{
              width: '100%',
              padding: '15px',
              fontSize: '18px',
              fontWeight: 'bold',
              opacity: (processing || !order.totalAmount || order.paymentStatus === 'Payé') ? 0.6 : 1,
              cursor: (processing || !order.totalAmount || order.paymentStatus === 'Payé') ? 'not-allowed' : 'pointer',
            }}
          >
            {processing ? 'Traitement en cours...' : `Payer $${(order.finalAmount || order.totalAmount)?.toFixed(2) || '0.00'}`}
          </button>

          <button
            className="btn"
            onClick={() => navigate('/dashboard')}
            style={{
              width: '100%',
              marginTop: '15px',
              padding: '12px',
              background: '#f0f0f0',
              border: '1px solid #ddd',
            }}
          >
            Annuler
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Payment;

